import { normalizeHeader } from './parseCsv';

export type DetectedExchange = 'cdc' | 'binance' | 'coinbase' | 'unknown';

export type ExchangeSchemaDetection = {
  exchange: DetectedExchange;
  schemaFamily: string;
  score: number;
  headersNorm: string[];
};

function tokenizeHeaderLine(line: string): string[] {
  return line.split(',').map((c) => {
    const t = c.trim();
    if (t.startsWith('"') && t.endsWith('"')) {
      return normalizeHeader(t.slice(1, -1).replace(/""/g, '"'));
    }
    return normalizeHeader(t);
  });
}

/**
 * Inspect the first line as CSV header + optional filename hint.
 */
export function detectExchangeCsvSchema(
  csvText: string,
  filenameHint?: string
): ExchangeSchemaDetection {
  const lines = csvText.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const headerLine = lines[0] ?? '';
  const headersNorm = tokenizeHeaderLine(headerLine);
  const joined = `|${headersNorm.join('|')}|`;
  const hint = (filenameHint ?? '').toLowerCase();

  const has = (name: string) => headersNorm.includes(name);
  const hasRe = (re: RegExp) => re.test(joined);

  type Cand = { exchange: DetectedExchange; schemaFamily: string; score: number };
  const cands: Cand[] = [];

  /** Crypto.com */
  let cdc = 0;
  if (has('utc_time') || has('timestamp_utc') || hasRe(/timestamp/) || hasRe(/utc_time/)) cdc += 4;
  if (has('transaction_kind') || has('transaction_type')) cdc += 4;
  if (has('to_currency') || has('to_amount')) cdc += 2;
  if (has('native_currency')) cdc += 1;
  if (hint.includes('crypto') && hint.includes('com')) cdc += 1;
  cands.push({ exchange: 'cdc', schemaFamily: 'crypto_com_transaction_export', score: cdc });

  /** Binance spot — Pair + Side/BuySell + executed qty */
  const binanceSpotLike =
    (has('pair') || hasRe(/\btrading_pair\b/)) &&
    (has('side') || has('type') || has('order_type')) &&
    (has('executed') || has('quantity') || has('filled'));
  let bnSpot = 0;
  if (binanceSpotLike) bnSpot += 10;
  if (has('price') || has('avgprice') || has('average_price')) bnSpot += 2;
  if (hint.includes('binance') && hint.includes('trade')) bnSpot += 2;
  if (has('operation') && has('change')) bnSpot -= 8;
  cands.push({ exchange: 'binance', schemaFamily: 'binance_spot_trade_history', score: bnSpot });

  /** Binance transaction / asset history */
  let bnTx = 0;
  if (has('operation')) bnTx += 5;
  if (has('coin') && has('change')) bnTx += 5;
  if (has('utc_time') || has('time') || has('insert_time') || hasRe(/date.*utc/)) bnTx += 2;
  if (has('account')) bnTx += 1;
  if (hint.includes('binance')) bnTx += 1;
  if (binanceSpotLike && !has('operation')) bnTx -= 6;
  cands.push({ exchange: 'binance', schemaFamily: 'binance_transaction_history', score: bnTx });

  /** Coinbase Taxes CSV */
  let cbTax = 0;
  if (has('transaction_type') || hasRe(/transactiontype/)) cbTax += 5;
  if (has('quantity_transacted') || hasRe(/quantity.*transacted/)) cbTax += 4;
  if (hasRe(/cost|proceeds|subtotal|fees/)) cbTax += 2;
  if (hint.includes('coinbase') && hint.includes('tax')) cbTax += 2;
  cands.push({ exchange: 'coinbase', schemaFamily: 'coinbase_taxes_transaction_report', score: cbTax });

  /** Coinbase Exchange / Pro fills */
  let cbFills = 0;
  if (has('trade_id') || has('tradeid')) cbFills += 5;
  if (has('product_id') || has('product')) cbFills += 3;
  if (has('size') && has('price')) cbFills += 3;
  if (hint.includes('fill')) cbFills += 2;
  cands.push({ exchange: 'coinbase', schemaFamily: 'coinbase_exchange_fills', score: cbFills });

  /** Coinbase account statement (weak) */
  let cbAcct = 0;
  if (hasRe(/balance|portfolio|account_type/)) cbAcct += 3;
  if (hint.includes('coinbase') && hint.includes('account')) cbAcct += 2;
  cands.push({ exchange: 'coinbase', schemaFamily: 'coinbase_account_statement', score: cbAcct });

  let best: Cand = { exchange: 'unknown', schemaFamily: 'unknown', score: 0 };
  for (const c of cands) {
    if (c.score > best.score) best = c;
  }

  if (best.score < 5) {
    return { exchange: 'unknown', schemaFamily: 'unknown', score: best.score, headersNorm };
  }

  return {
    exchange: best.exchange,
    schemaFamily: best.schemaFamily,
    score: best.score,
    headersNorm,
  };
}
